import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GenericApiOption } from '@core/generic-api/data-access';
import { GenericStorageApiService, StoragePublicLinkDto, StorageUploadLink } from '@portal/storage-api/data-access';
import { catchError, combineLatest, map, Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ApplicationModel, AttachmentModel, BeneficialOwnerModel, PersonnelModel, UsPersonModel } from '../model';
import { ApplicationUploadLinkDto } from '../dto';
import { ApplicationFacade, DataAccessStore, PersonnelFacade } from '../store';
import { CrsTaxResidenceModel } from '@core/data-access';
import { SKIP_RECAPTCHA_INTERCEPTOR } from '@core/recaptcha/data-access';
import { parseAddressesToPersonnelAddress } from '../util';

export interface ApplicationErrorResponse {
  statusCode: number;
  error: string;
  errorCode: string;
}

@Injectable({ providedIn: 'root' })
export class ApplicationApiService extends GenericStorageApiService {
  private readonly applicationFacade = inject(ApplicationFacade);
  private readonly personnelFacade = inject(PersonnelFacade);
  private readonly dataAccessStore = inject(DataAccessStore);
  readonly UPLOAD_LINK = 'case-application-external/upload-link';
  readonly PUBLIC_LINK = 'case-application-external/link';

  getUploadLink(dto: ApplicationUploadLinkDto, requestOption?: GenericApiOption): Observable<StorageUploadLink> {
    let headers = this.api.createHeaders();

    if (requestOption?.requestOptions?.headers) {
      if (requestOption.requestOptions.headers instanceof HttpHeaders) {
        headers = requestOption.requestOptions.headers;
      } else {
        headers = new HttpHeaders(requestOption.requestOptions.headers);
      }
    }

    headers.set(SKIP_RECAPTCHA_INTERCEPTOR, 'true');

    return this.api
      .post(this.UPLOAD_LINK, dto, { ...requestOption, requestOptions: { headers } })
      .pipe(map(res => StorageUploadLink.fromJson(res)));
  }

  getPublicLink(data: StoragePublicLinkDto, requestOption?: GenericApiOption): Observable<string> {
    return this.dataAccessStore.accessToken$.pipe(
      switchMap(accessToken =>
        this.api.get(this.PUBLIC_LINK, data, {
          ...requestOption,
          requestOptions: { headers: this.getHeaders(accessToken) },
        }),
      ),
    );
  }

  getApplication(appId: string, accessToken: string): Observable<ApplicationModel> {
    return combineLatest<[ApplicationModel, PersonnelModel[], BeneficialOwnerModel[], UsPersonModel[]]>([
      this.api
        .get(this.getEndpoint(appId), {}, { requestOptions: { headers: this.getHeaders(accessToken) } })
        .pipe(map(res => ApplicationModel.fromJson(res))),
      this.api.get(this.getEndpoint(appId, 'key-personnels'), null, { requestOptions: { headers: this.getHeaders(accessToken) } }).pipe(
        map(res =>
          res.map((v: object) => {
            const personnel = PersonnelModel.fromJson(v);
            const address = parseAddressesToPersonnelAddress({
              addresses: personnel.addresses,
              mailingAddressDifferent: personnel.metadata.addressMetadata?.maillingAddressDifferent || false,
            });

            personnel.uuid = personnel.metadata?.uuid || null;
            personnel.address = address;

            return personnel;
          }),
        ),
      ),
      this.api
        .get(this.getEndpoint(appId, 'beneficial-owners'), null, { requestOptions: { headers: this.getHeaders(accessToken) } })
        .pipe(map(res => res.map((v: object) => BeneficialOwnerModel.fromJson(v)))),
      this.api
        .get(this.getEndpoint(appId, 'us-persons'), null, { requestOptions: { headers: this.getHeaders(accessToken) } })
        .pipe(map(res => res.map((v: object) => UsPersonModel.fromJson(v)))),
    ]).pipe(
      map(([application, personnels, beneficialOwners, usPersons]) => {
        const personnelsSorted: PersonnelModel[] = this.sortKeyPersonnels(personnels);

        this.personnelFacade.init({ personnels: personnelsSorted, accessToken }); // TODO: REMOVE THIS

        application.crs = this.setAndSortTaxResidenceByOrder(application.crs || []);
        application.keyPersonnels = personnelsSorted;

        application.beneficialOwners = (beneficialOwners || []).sort(this.sortByOrder);
        application.usPersons = (usPersons || []).sort(this.sortByOrder);
        this.applicationFacade.setApplication(ApplicationModel.fromJson(application));
        return application;
      }),
    );
  }

  getApplicationAttachments(appId: string, accessToken?: string): Observable<AttachmentModel[]> {
    return this.api.get(
      this.getEndpoint(appId, 'attachments'),
      {},
      {
        requestOptions: { headers: this.getHeaders(accessToken) },
      },
    );
  }

  submit(data: any, accessToken?: string) {
    return this.api.post(this.getEndpoint(), data, {
      requestOptions: { headers: this.getHeaders(accessToken) },
      excludeFields: ['usPersons', 'crsEntity', 'crs', 'beneficialOwners', 'attachment'],
    });
  }

  getBoardResolutionFile(appId: string, accessToken: string): Observable<Blob> {
    return this.api
      .get(
        this.getEndpoint(appId, 'board-resolution'),
        {},
        {
          requestOptions: { responseType: 'arraybuffer', headers: this.getHeaders(accessToken) },
          pretreatmentResponse: false,
        },
      )
      .pipe(
        map(buffer => new Blob([buffer], { type: 'application/pdf' })),
        catchError((error: HttpErrorResponse) => {
          let parsedError: ApplicationErrorResponse | undefined;

          if (error.error instanceof ArrayBuffer) {
            // Decode the ArrayBuffer error into JSON
            const textDecoder = new TextDecoder('utf-8');
            const errorText = textDecoder.decode(error.error);
            parsedError = JSON.parse(errorText);
          } else {
            // Handle any other type of error if necessary
            parsedError = {
              statusCode: error.status,
              error: error.statusText,
              errorCode: 'UNKNOWN',
            };
          }

          return throwError(() => parsedError);
        }),
      );
  }

  private getEndpoint(...endpoint: string[]): string {
    return ['case-application-external', ...endpoint].join('/');
  }

  private getHeaders(accessToken?: string | null): HttpHeaders {
    return accessToken ? this.api.createHeaders().set('Authorization', `Bearer ${accessToken}`) : this.api.createHeaders();
  }

  private sortByOrder(a: any, b: any): number {
    return a.metadata?.order - b.metadata?.order;
  }

  private setAndSortTaxResidenceByOrder(crs: CrsTaxResidenceModel[]): CrsTaxResidenceModel[] {
    return (
      crs
        ?.map((c, i) => {
          c.metadata = { ...c.metadata, order: typeof c.metadata?.order === 'number' ? c.metadata?.order : i + 1 };
          return c;
        })
        .sort(this.sortByOrder) || []
    );
  }

  private sortKeyPersonnels(personnels: PersonnelModel[]) {
    return personnels
      ?.map(m => {
        m.crs = this.setAndSortTaxResidenceByOrder(m.crs || []);
        return m;
      })
      ?.sort(this.sortByOrder);
  }
}
